import { type Path, type XY } from '../types';

const DEFAULT_WOBBLE = 12;

const lerp = (a: XY, b: XY, t: number): XY => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
});

const quadraticBezier = (p0: XY, p1: XY, p2: XY, t: number): XY => {
  const u = 1 - t;
  return {
    x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
    y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
  };
};

const subdividePoly = (pts: XY[]): XY[] => {
  if (pts.length < 2) return pts;

  const detailed: XY[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const from = pts[i];
    const to = pts[i + 1];
    const segLen = distance(from, to);
    const steps = Math.max(6, Math.ceil(segLen / 80));

    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const point = lerp(from, to, t);
      if (i === 0 && s === 0) {
        detailed.push(point);
      } else {
        const prev = detailed[detailed.length - 1];
        if (distance(prev, point) > 0.5) {
          detailed.push(point);
        }
      }
    }
  }

  return detailed;
};

const samplePath = (path: Path, samples = 24): XY[] => {
  if (path.mode === 'poly') {
    return subdividePoly(path.pts);
  }
  if (path.mode === 'spline') {
    return path.pts;
  }

  if (path.mode === 'quad') {
    const pts: XY[] = [];
    for (let i = 0; i < samples; i++) {
      const t = samples === 1 ? 0 : i / (samples - 1);
      pts.push(quadraticBezier(path.p0, path.p1, path.p2, t));
    }
    return pts;
  }

  return [];
};

const distance = (a: XY, b: XY): number => Math.hypot(b.x - a.x, b.y - a.y);

export const buildDribblePath = (path: Path, wobble = DEFAULT_WOBBLE): Path => {
  const basePts = samplePath(path);
  if (basePts.length < 2) return path;

  let totalLen = 0;
  for (let i = 0; i < basePts.length - 1; i++) {
    totalLen += distance(basePts[i], basePts[i + 1]);
  }
  if (totalLen === 0) return path;

  const waves = Math.max(2, Math.round((totalLen / 15) * 3));
  const wiggled: XY[] = [];
  let traveled = 0;

  for (let i = 0; i < basePts.length; i++) {
    const prev = basePts[Math.max(0, i - 1)];
    const curr = basePts[i];
    const next = basePts[Math.min(basePts.length - 1, i + 1)];

    if (i > 0) {
      traveled += distance(prev, curr);
    }

    const tangent = { x: next.x - prev.x, y: next.y - prev.y };
    const len = Math.hypot(tangent.x, tangent.y) || 1;
    const normal = { x: -tangent.y / len, y: tangent.x / len };

    const progress = traveled / totalLen;
    const offsetAmount = Math.sin(progress * Math.PI * waves) * wobble;
    const offset =
      i === 0 || i === basePts.length - 1
        ? { x: 0, y: 0 }
        : { x: normal.x * offsetAmount, y: normal.y * offsetAmount };

    wiggled.push({ x: curr.x + offset.x, y: curr.y + offset.y });
  }

  return { mode: 'spline', pts: wiggled };
};
